import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { formatTimeString, formatDateForInput, parseDateOnlyString, formatDate as formatDateUtil } from '../../utils/dateUtils';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Autocomplete,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Class as ClassIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../context/AuthContext';
import { classAPI, classTypeAPI, classLevelAPI, membershipTierAPI, userAPI } from '../../services/api';
import { Class, ClassType, ClassLevel, RecurringPattern, BookingWindowConfiguration as BookingWindowConfigurationType, MembershipTier, User, TierAccessLevel, TierAccessConfiguration, DeleteClassResponse } from '../../types';

const EditClassPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Determine if we're creating a new class or editing an existing one
  const isEditing = Boolean(classId);

  // State
  const [classData, setClassData] = useState<Class | null>(null);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [classLevels, setClassLevels] = useState<ClassLevel[]>([]);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Delete confirmation dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletePreview, setDeletePreview] = useState<DeleteClassResponse | null>(null);
  const [loadingDeletePreview, setLoadingDeletePreview] = useState(false);

  const DEFAULT_CLASS_DURATION = 60; // in minutes

  // Form state
  const [formData, setFormData] = useState(() => {
    const defaultStartTime = new Date();
    const defaultEndTime = new Date(defaultStartTime);
    defaultEndTime.setMinutes(defaultStartTime.getMinutes() + DEFAULT_CLASS_DURATION);

    return {
      name: '',
      type: '',
      newType: '',
      description: '',
      coachId: '',
      instructorName: '',
      isGuestCoach: false,
      venue: '',
      level: '',
      newLevel: '',
      capacity: 0,
      scheduleDate: new Date(),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      duration: DEFAULT_CLASS_DURATION,
      oneTimeCost: '',
      active: true,
      isWaitlistAvailable: true,
      recurringPattern: null as RecurringPattern | null,
      isRecurring: false,
      bookingWindows: {} as BookingWindowConfigurationType,
      tierAccess: {} as TierAccessConfiguration
    };
  });

  // Track if class has bookings to prevent recurring pattern changes
  const [hasBookings, setHasBookings] = useState(false);

  // Collapsible section states
  const [bookingConfigExpanded, setBookingConfigExpanded] = useState(false);
  const [tierAccessExpanded, setTierAccessExpanded] = useState(false);

  // Booking window configuration state
  const [bookingWindowCards, setBookingWindowCards] = useState<Array<{
    id: string;
    membershipTier: string;
    unlimited: boolean;
    days: number;
  }>>([]);

  // Tier access configuration state
  const [tierAccessCards, setTierAccessCards] = useState<Array<{
    id: string;
    membershipTier: string;
    accessLevel: TierAccessLevel;
  }>>([]);

  // Helper function to parse time strings that might include timezone info
  const parseTimeForEdit = (timeString: string): Date => {
    if (!timeString) return new Date();

    // Handle time with timezone (e.g., "20:00:00+00") or without timezone (e.g., "20:00:00")
    const timeOnly = timeString.split('+')[0].split('-')[0]; // Remove timezone if present
    const [hours, minutes, seconds] = timeOnly.split(':').map(Number);

    const date = new Date();
    date.setHours(hours, minutes, seconds || 0, 0);
    return date;
  };

  // Helper function to calculate duration in minutes
  const calculateDuration = (startTime: Date, endTime: Date): number => {
    const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
    const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
    let duration = endMinutes - startMinutes;

    // Handle case where end time is next day (e.g., 11:30 PM to 1:00 AM)
    if (duration < 0) {
      duration += 24 * 60; // Add 24 hours
    }

    return Math.max(duration, 0); // Ensure non-negative duration
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchClassTypes(),
          fetchClassLevels(),
          fetchMembershipTiers(),
          fetchCoaches()
        ]);

        if (isEditing && classId) {
          await fetchClassData();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId, isEditing]);

  const fetchClassData = async () => {
    if (!classId) return;

    try {
      const response = await classAPI.getClassById(classId);
      const classItem = response.data;
      setClassData(classItem);
      
      // Check if class has bookings to prevent recurring pattern changes
      setHasBookings(classItem.currentBookings > 0);

      // Populate form with existing class data
      const startTime = parseTimeForEdit(classItem.startTime);
      const endTime = parseTimeForEdit(classItem.endTime);
      const duration = calculateDuration(startTime, endTime);
      const scheduleDateParsed = parseDateOnlyString(classItem.scheduleDate);

      setFormData({
        name: classItem.name,
        type: classItem.type,
        newType: '',
        description: classItem.description || '',
        coachId: classItem.coachId || '',
        instructorName: classItem.instructorName || classItem.instructor || '',
        isGuestCoach: classItem.isGuestCoach || !classItem.coachId,
        venue: classItem.venue,
        level: classItem.level,
        newLevel: '',
        capacity: classItem.capacity,
        scheduleDate: scheduleDateParsed,
        startTime,
        endTime,
        duration,
        oneTimeCost: classItem.oneTimeCost?.toString() || '',
        active: classItem.active,
        isWaitlistAvailable: classItem.isWaitlistAvailable ?? true,
        recurringPattern: (() => {
          if (!classItem.recurringPattern) return null;
          return {
            frequency: classItem.recurringPattern.frequency || 'weekly',
            interval: classItem.recurringPattern.interval || 1,
            daysOfWeek: classItem.recurringPattern.daysOfWeek || [],
            endDate: classItem.recurringPattern.endDate || undefined
          };
        })(),
        isRecurring: !!classItem.recurringPattern,
        bookingWindows: classItem.bookingWindows || {},
        tierAccess: classItem.tierAccess || {}
      });

      // Initialize booking window cards from existing data
      const existingWindows = classItem.bookingWindows || {};
      const windowCards = Object.entries(existingWindows).map(([tierName, windowConfig], index) => ({
        id: `card-${Date.now()}-${index}`,
        membershipTier: tierName,
        unlimited: (windowConfig as any).unlimited || false,
        days: (windowConfig as any).advanceBookingDays || 7
      }));
      setBookingWindowCards(windowCards);
      setBookingConfigExpanded(Object.keys(existingWindows).length > 0);

      // Initialize tier access cards from existing data
      const existingAccess = classItem.tierAccess || {};
      const accessCards = Object.entries(existingAccess).map(([tierName, accessLevel], index) => ({
        id: `tier-${Date.now()}-${index}`,
        membershipTier: tierName,
        accessLevel: accessLevel as TierAccessLevel
      }));
      setTierAccessCards(accessCards);
      setTierAccessExpanded(Object.keys(existingAccess).length > 0);
    } catch (error: any) {
      console.error('Error fetching class:', error);
      setError('Failed to load class data');
    }
  };

  const fetchClassTypes = async () => {
    try {
      const response = await classTypeAPI.getAll();
      setClassTypes(response.data);
    } catch (error: any) {
      console.error('Error fetching class types:', error);
    }
  };

  const fetchClassLevels = async () => {
    try {
      const response = await classLevelAPI.getAll();
      setClassLevels(response.data);
    } catch (error: any) {
      console.error('Error fetching class levels:', error);
    }
  };

  const fetchMembershipTiers = async () => {
    try {
      const response = await membershipTierAPI.getMembershipTiers();
      setMembershipTiers(response.data);
    } catch (error: any) {
      console.error('Error fetching membership tiers:', error);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await userAPI.getCoaches();
      setCoaches(response.data);
    } catch (error: any) {
      console.error('Error fetching coaches:', error);
    }
  };

  // Booking window card management functions
  const addBookingWindowCard = () => {
    const newCard = {
      id: `card-${Date.now()}`,
      membershipTier: '',
      unlimited: false,
      days: 7
    };
    setBookingWindowCards([...bookingWindowCards, newCard]);
  };

  const removeBookingWindowCard = (cardId: string) => {
    const updatedCards = bookingWindowCards.filter(card => card.id !== cardId);
    setBookingWindowCards(updatedCards);

    // Update form data - remove the booking window for this card's membership tier
    const cardToRemove = bookingWindowCards.find(card => card.id === cardId);
    if (cardToRemove && cardToRemove.membershipTier) {
      const updatedBookingWindows = { ...formData.bookingWindows };
      delete updatedBookingWindows[cardToRemove.membershipTier];
      setFormData({ ...formData, bookingWindows: updatedBookingWindows });
    }
  };

  const updateBookingWindowCard = (cardId: string, field: string, value: any) => {
    const updatedCards = bookingWindowCards.map(card => {
      if (card.id === cardId) {
        const updatedCard = { ...card, [field]: value };

        // If membership tier changed, remove the old booking window from formData
        if (field === 'membershipTier' && card.membershipTier && card.membershipTier !== value) {
          const { [card.membershipTier]: removed, ...remainingWindows } = formData.bookingWindows;
          setFormData({
            ...formData,
            bookingWindows: remainingWindows
          });
        }

        // Update formData with the new booking window configuration
        if (updatedCard.membershipTier) {
          setFormData({
            ...formData,
            bookingWindows: {
              ...formData.bookingWindows,
              [updatedCard.membershipTier]: {
                advanceBookingDays: updatedCard.unlimited ? 0 : updatedCard.days,
                unlimited: updatedCard.unlimited
              }
            }
          });
        }

        return updatedCard;
      }
      return card;
    });
    setBookingWindowCards(updatedCards);
  };

  // Tier access card management functions
  const addTierAccessCard = () => {
    const newCard = {
      id: `tier-${Date.now()}`,
      membershipTier: '',
      accessLevel: TierAccessLevel.FREE
    };
    setTierAccessCards([...tierAccessCards, newCard]);
  };

  const removeTierAccessCard = (cardId: string) => {
    const updatedCards = tierAccessCards.filter(card => card.id !== cardId);
    setTierAccessCards(updatedCards);
    
    // Update form data - remove the tier access for this card's membership tier
    const cardToRemove = tierAccessCards.find(card => card.id === cardId);
    if (cardToRemove && cardToRemove.membershipTier) {
      const { [cardToRemove.membershipTier]: removed, ...remainingAccess } = formData.tierAccess;
      setFormData({
        ...formData,
        tierAccess: remainingAccess
      });
    }
  };

  const updateTierAccessCard = (cardId: string, field: string, value: any) => {
    const updatedCards = tierAccessCards.map(card => {
      if (card.id === cardId) {
        const updatedCard = { ...card, [field]: value };
        
        // If membership tier changed, remove the old tier access from formData
        if (field === 'membershipTier' && card.membershipTier && card.membershipTier !== value) {
          const { [card.membershipTier]: removed, ...remainingAccess } = formData.tierAccess;
          setFormData({
            ...formData,
            tierAccess: remainingAccess
          });
        }
        
        // Update formData with the new tier access configuration
        if (updatedCard.membershipTier) {
          setFormData({
            ...formData,
            tierAccess: {
              ...formData.tierAccess,
              [updatedCard.membershipTier]: updatedCard.accessLevel
            }
          });
        }
        
        return updatedCard;
      }
      return card;
    });
    setTierAccessCards(updatedCards);
  };

  // Helper function to handle duration change
  const handleDurationChange = (durationMinutes: number) => {
    const newEndTime = new Date(formData.startTime);
    newEndTime.setMinutes(formData.startTime.getMinutes() + durationMinutes);

    setFormData({
      ...formData,
      duration: durationMinutes,
      endTime: newEndTime
    });
  };

  // Helper function to handle start time change
  const handleStartTimeChange = (newStartTime: Date) => {
    const newEndTime = new Date(newStartTime);
    newEndTime.setMinutes(newStartTime.getMinutes() + formData.duration);

    setFormData({
      ...formData,
      startTime: newStartTime,
      endTime: newEndTime
    });
  };

  // Helper function to handle end time change
  const handleEndTimeChange = (newEndTime: Date) => {
    const duration = calculateDuration(formData.startTime, newEndTime);

    setFormData({
      ...formData,
      endTime: newEndTime,
      duration: duration
    });
  };

  const handleClassDayOfWeekChange = (day: number, checked: boolean) => {
    if (!formData.recurringPattern) return;

    const scheduleDateDayOfWeek = formData.scheduleDate.getDay();

    // Prevent unchecking the class date's day
    if (day === scheduleDateDayOfWeek && !checked) {
      return;
    }

    const currentDays = formData.recurringPattern.daysOfWeek || [];
    let updatedDays;

    if (checked) {
      updatedDays = [...currentDays, day].sort();
    } else {
      updatedDays = currentDays.filter(d => d !== day);
    }

    setFormData({
      ...formData,
      recurringPattern: {
        ...formData.recurringPattern,
        daysOfWeek: updatedDays
      }
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);

    // Validation
    if (!formData.name.trim()) {
      setError('Class name is required');
      setSubmitting(false);
      return;
    }

    if (!formData.type.trim()) {
      setError('Class type is required');
      setSubmitting(false);
      return;
    }

    if (!formData.level.trim()) {
      setError('Class level is required');
      setSubmitting(false);
      return;
    }

    if (!formData.isGuestCoach && !formData.coachId) {
      setError('Please select a coach or enable guest coach option');
      setSubmitting(false);
      return;
    }

    if (formData.isGuestCoach && !formData.instructorName.trim()) {
      setError('Guest coach name is required');
      setSubmitting(false);
      return;
    }

    if (!formData.venue.trim()) {
      setError('Venue is required');
      setSubmitting(false);
      return;
    }

    if (!formData.capacity || formData.capacity <= 0) {
      setError('Capacity must be greater than 0');
      setSubmitting(false);
      return;
    }

    // Validate schedule date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
    const scheduleDate = new Date(formData.scheduleDate);
    scheduleDate.setHours(0, 0, 0, 0);
    
    if (scheduleDate < today) {
      setError('Class date cannot be in the past');
      setSubmitting(false);
      return;
    }

    // Validate time logic
    if (formData.startTime >= formData.endTime) {
      setError('End time must be after start time');
      setSubmitting(false);
      return;
    }

    try {
      const startTime = formData.startTime.toTimeString().split(' ')[0].slice(0, 5);
      const endTime = formData.endTime.toTimeString().split(' ')[0].slice(0, 5);

      const classData = {
        name: formData.name,
        type: formData.newType || formData.type,
        description: formData.description,
        coachId: formData.isGuestCoach ? null : formData.coachId,
        instructorName: formData.instructorName,
        isGuestCoach: formData.isGuestCoach,
        venue: formData.venue,
        level: formData.newLevel || formData.level,
        capacity: formData.capacity,
        scheduleDate: formatDateForInput(formData.scheduleDate),
        startTime,
        endTime,
        duration: formData.duration,
        oneTimeCost: formData.oneTimeCost ? parseFloat(formData.oneTimeCost) : null,
        active: formData.active,
        isWaitlistAvailable: formData.isWaitlistAvailable,
        recurringPattern: formData.isRecurring && formData.recurringPattern ? {
          frequency: formData.recurringPattern.frequency,
          interval: formData.recurringPattern.interval,
          daysOfWeek: formData.recurringPattern.daysOfWeek,
          endDate: formData.recurringPattern.endDate || undefined
        } : null,
        bookingWindows: Object.keys(formData.bookingWindows).length > 0 ? formData.bookingWindows : undefined,
        tierAccess: Object.keys(formData.tierAccess).length > 0 ? formData.tierAccess : undefined
      };

      if (isEditing && classId) {
        await classAPI.updateClass(classId, classData);
        setSuccessMessage('Class updated successfully!');
      } else {
        await classAPI.createClass(classData);
        setSuccessMessage('Class created successfully!');
      }

      // Navigate back after short delay
      setTimeout(() => {
        navigate(-1);
      }, 1500);

    } catch (error: any) {
      console.error('Error saving class:', error);
      setError(error.response?.data?.message || 'Failed to save class');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!isEditing || !classId) return;
    setOpenDeleteDialog(true);
  };

  const confirmDeleteClass = async () => {
    if (!classId) return;

    try {
      const response = await classAPI.deleteClass(classId);
      setDeletePreview(response.data);
      setSuccessMessage(`Class deleted successfully! ${response.data.summary.totalMembersNotified > 0 ? `${response.data.summary.totalMembersNotified} member(s) have been notified.` : ''}`);
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (error: any) {
      console.error('Error deleting class:', error);
      setError(error.response?.data?.message || 'Failed to delete class');
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const canEditClass = () => {
    return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  };

  if (!canEditClass()) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">You don't have permission to {isEditing ? 'edit' : 'create'} classes.</Alert>
          <Button
            startIcon={<CloseIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              color="inherit"
              startIcon={<HomeIcon />}
              sx={{ textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Button
              component={RouterLink}
              to="/class-management"
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Class Management
            </Button>
            <Typography color="text.primary">
              {isEditing ? 'Edit Class' : 'Create Class'}
            </Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <ClassIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {isEditing ? 'Edit Class' : 'Create New Class'}
            </Typography>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => navigate(-1)}
              >
                <CloseIcon />
              </Button>
              {isEditing && (
                <Button
                  onClick={handleDelete}
                  color="error"
                >
                  <DeleteIcon />
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={submitting || !formData.name || !formData.type || !formData.level || (!formData.isGuestCoach && !formData.coachId) || (formData.isGuestCoach && !formData.instructorName) || !formData.venue || !formData.capacity || formData.capacity <= 0}
              >
                {/* {submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')} Class */}
                <SaveIcon />
              </Button>
            </Stack>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Main Form */}
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {/* Class Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Class Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              {/* Coach Selection */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Autocomplete
                    options={coaches}
                    getOptionLabel={(coach) => coach.firstName && coach.lastName ? `${coach.firstName} ${coach.lastName}` : coach.email || 'Unknown Coach'}
                    value={coaches.find(coach => coach.id === formData.coachId) || null}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        coachId: newValue?.id || '',
                        instructorName: newValue ? (newValue.firstName && newValue.lastName ? `${newValue.firstName} ${newValue.lastName}` : newValue.email || 'Unknown Coach') : formData.instructorName
                      });
                    }}
                    disabled={formData.isGuestCoach}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Coach"
                        required={!formData.isGuestCoach}
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isGuestCoach}
                        onChange={(e) => setFormData({
                          ...formData,
                          isGuestCoach: e.target.checked,
                          coachId: e.target.checked ? '' : formData.coachId
                        })}
                      />
                    }
                    label="Guest Coach/Instructor"
                  />
                  {formData.isGuestCoach && (
                    <TextField
                      label="Guest Coach Name"
                      value={formData.instructorName}
                      onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })}
                      fullWidth
                      required
                      placeholder="Enter coach/instructor name"
                    />
                  )}
                </Stack>
              </Grid>

              {/* Class Type */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  freeSolo
                  options={classTypes.map((type) => type.name)}
                  value={formData.type}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, type: newValue || '', newType: '' });
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData({ ...formData, type: newInputValue, newType: newInputValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Class Type"
                      helperText="Select existing or type new"
                      required
                    />
                  )}
                />
              </Grid>

              {/* Class Level */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  freeSolo
                  options={classLevels.map((level) => level.name)}
                  value={formData.level}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, level: newValue || '', newLevel: '' });
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData({ ...formData, level: newInputValue, newLevel: newInputValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Class Level"
                      helperText="Select existing or type new"
                      required
                    />
                  )}
                />
              </Grid>

              {/* Venue */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Venue"
                  value={formData.venue}
                  onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              {/* Capacity */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>

              {/* Schedule Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Date"
                  value={formData.scheduleDate}
                  minDate={new Date()}
                  onChange={(date) => {
                    const newDate = date || new Date();

                    // If we have weekly recurrence enabled, auto-include the new date's day
                    if (formData.isRecurring && formData.recurringPattern?.frequency === 'weekly') {
                      const newDayOfWeek = newDate.getDay();
                      const currentDays = formData.recurringPattern.daysOfWeek || [];

                      // Add the new day if it's not already included
                      if (!currentDays.includes(newDayOfWeek)) {
                        const updatedDays = [...currentDays, newDayOfWeek].sort();
                        setFormData({
                          ...formData,
                          scheduleDate: newDate,
                          recurringPattern: {
                            ...formData.recurringPattern,
                            daysOfWeek: updatedDays
                          }
                        });
                        return;
                      }
                    }

                    setFormData({ ...formData, scheduleDate: newDate });
                  }}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* Duration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleDurationChange(parseInt(e.target.value) || 60)}
                  fullWidth
                  required
                  inputProps={{ min: 15, max: 480 }}
                  helperText="Duration in minutes (15-480)"
                />
              </Grid>

              {/* Start Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="Start Time"
                  value={formData.startTime}
                  onChange={(time) => handleStartTimeChange(time || new Date())}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* End Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="End Time"
                  value={formData.endTime}
                  onChange={(time) => handleEndTimeChange(time || new Date())}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* One Time Cost */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="One-time Cost"
                  type="number"
                  value={formData.oneTimeCost}
                  onChange={(e) => setFormData({ ...formData, oneTimeCost: e.target.value })}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Active Status */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Waitlist Available */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isWaitlistAvailable}
                      onChange={async (e) => {
                        // If trying to disable waitlist, check if there are waitlisted users
                        if (!e.target.checked && isEditing && classId) {
                          try {
                            // Fetch current attendees to check waitlist
                            const attendeesResponse = await classAPI.getClassBookings(classId);
                            const waitlistCount = attendeesResponse.data.waitlistCount || 0;

                            if (waitlistCount > 0) {
                              setError(`Cannot disable waitlist - there are ${waitlistCount} user(s) currently on the waitlist. Please handle the waitlisted users first.`);
                              return;
                            }
                          } catch (error) {
                            console.error('Error checking waitlist:', error);
                            // Continue anyway - backend validation will catch it
                          }
                        }
                        setError(null);
                        setFormData({ ...formData, isWaitlistAvailable: e.target.checked });
                      }}
                    />
                  }
                  label="Allow Waitlist"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Recurring Pattern */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isRecurring}
                      disabled={hasBookings}
                      onChange={(e) => setFormData({
                        ...formData,
                        isRecurring: e.target.checked,
                        recurringPattern: e.target.checked ? {
                          frequency: 'weekly',
                          interval: 1,
                          daysOfWeek: [formData.scheduleDate.getDay()]
                        } : null
                      })}
                    />
                  }
                  label="Recurring Class"
                />
                {hasBookings && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                    Cannot change recurring pattern - this class has registered members
                  </Typography>
                )}
              </Grid>

              {formData.isRecurring && (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={formData.recurringPattern?.frequency || 'weekly'}
                        onChange={(e) => {
                          const newFrequency = e.target.value as 'daily' | 'weekly' | 'monthly';
                          let updatedPattern = {
                            ...formData.recurringPattern!,
                            frequency: newFrequency
                          };

                          // If switching to weekly, ensure schedule date's day is included
                          if (newFrequency === 'weekly') {
                            const scheduleDateDayOfWeek = formData.scheduleDate.getDay();
                            const currentDays = formData.recurringPattern?.daysOfWeek || [];

                            if (!currentDays.includes(scheduleDateDayOfWeek)) {
                              updatedPattern.daysOfWeek = [...currentDays, scheduleDateDayOfWeek].sort();
                            }
                          }

                          setFormData({
                            ...formData,
                            recurringPattern: updatedPattern
                          });
                        }}
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Interval"
                      type="number"
                      value={formData.recurringPattern?.interval || 1}
                      onChange={(e) => setFormData({
                        ...formData,
                        recurringPattern: {
                          ...formData.recurringPattern!,
                          interval: parseInt(e.target.value) || 1
                        }
                      })}
                      fullWidth
                      inputProps={{ min: 1, max: 12 }}
                      helperText={`Every ${formData.recurringPattern?.interval || 1} ${(formData.recurringPattern?.frequency || 'weekly').slice(0, -2) === 'dai' ? 'day' : (formData.recurringPattern?.frequency || 'weekly').slice(0, -2)}${(formData.recurringPattern?.interval || 1) > 1 ? 's' : ''}`}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <DatePicker
                      label="End Date (Optional)"
                      value={formData.recurringPattern?.endDate ? parseDateOnlyString(formData.recurringPattern.endDate) : null}
                      onChange={(date) => setFormData({
                        ...formData,
                        recurringPattern: {
                          ...formData.recurringPattern!,
                          endDate: date ? formatDateForInput(date) : undefined
                        }
                      })}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>

                  {/* Days of Week (for weekly) */}
                  {formData.recurringPattern?.frequency === 'weekly' && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Days of Week
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        The class date's day ({['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][formData.scheduleDate.getDay()]}) is automatically included and cannot be removed.
                      </Typography>
                      <FormGroup row>
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                          const isClassDateDay = index === formData.scheduleDate.getDay();
                          return (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  checked={formData.recurringPattern?.daysOfWeek?.includes(index) || false}
                                  onChange={(e) => handleClassDayOfWeekChange(index, e.target.checked)}
                                  disabled={isClassDateDay} // Disable the class date's day
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {day}
                                  {isClassDateDay && (
                                    <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                                      (Class Date)
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    </Grid>
                  )}
                </>
              )}
            </Grid>

            {/* Collapsible Booking Window Configuration */}
            <Accordion expanded={bookingConfigExpanded} onChange={() => setBookingConfigExpanded(!bookingConfigExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Booking Window Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <Typography variant="body2" color="text.secondary">
                    Configure booking windows for different membership tiers. Members can book this class up to the specified number of days in advance.
                  </Typography>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Booking Windows
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addBookingWindowCard}
                      >
                        Add Window
                      </Button>
                    </Box>

                    {bookingWindowCards.length === 0 ? (
                      <Alert severity="info">
                        No booking windows configured. Add booking windows to control how far in advance different membership tiers can book this class.
                      </Alert>
                    ) : (
                      <Stack spacing={2}>
                        {bookingWindowCards.map((card) => (
                          <Card key={card.id} variant="outlined">
                            <CardContent sx={{ pb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2">Booking Window</Typography>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => removeBookingWindowCard(card.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>

                              <Stack spacing={2}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                  <FormControl sx={{ flex: 1 }} size="small">
                                    <InputLabel>Membership Tier</InputLabel>
                                    <Select
                                      value={card.membershipTier}
                                      label="Membership Tier"
                                      onChange={(e) => updateBookingWindowCard(card.id, 'membershipTier', e.target.value)}
                                    >
                                      {membershipTiers
                                        .filter(tier => !bookingWindowCards.some(otherCard =>
                                          otherCard.id !== card.id && otherCard.membershipTier === tier.name
                                        ))
                                        .map((tier) => (
                                          <MenuItem key={tier.id} value={tier.name}>
                                            {tier.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>

                                  {!card.unlimited && (
                                    <TextField
                                      label="Advance Booking Days"
                                      type="number"
                                      value={card.days}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (!isNaN(value) && value > 0) {
                                          updateBookingWindowCard(card.id, 'days', value);
                                        }
                                      }}
                                      size="small"
                                      sx={{ width: 180 }}
                                      InputProps={{
                                        inputProps: { min: 1, max: 365 }
                                      }}
                                    />
                                  )}
                                </Box>

                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={card.unlimited}
                                      onChange={(e) => updateBookingWindowCard(card.id, 'unlimited', e.target.checked)}
                                    />
                                  }
                                  label="Unlimited advance booking"
                                />
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Collapsible Tier Access Configuration */}
            <Accordion expanded={tierAccessExpanded} onChange={() => setTierAccessExpanded(!tierAccessExpanded)} sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Tier Access Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      Configure access levels for different membership tiers. Members with "Free Access" can register without charge, while "Paid Access" requires payment.
                    </Typography>
                  </Alert>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Tier Access Levels
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addTierAccessCard}
                      >
                        Add Tier Access
                      </Button>
                    </Box>

                    {tierAccessCards.length === 0 ? (
                      <Alert severity="warning">
                        No tier access configured. All membership tiers will default to free access.
                      </Alert>
                    ) : (
                      <Stack spacing={2}>
                        {tierAccessCards.map((card) => (
                          <Card key={card.id} variant="outlined">
                            <CardContent sx={{ pb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2">Tier Access Rule</Typography>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => removeTierAccessCard(card.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>

                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Membership Tier</InputLabel>
                                    <Select
                                      value={card.membershipTier}
                                      label="Membership Tier"
                                      onChange={(e) => updateTierAccessCard(card.id, 'membershipTier', e.target.value)}
                                    >
                                      {membershipTiers
                                        .filter(tier => !tierAccessCards.some(otherCard =>
                                          otherCard.id !== card.id && otherCard.membershipTier === tier.name
                                        ))
                                        .map((tier) => (
                                          <MenuItem key={tier.id} value={tier.name}>
                                            {tier.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Access Level</InputLabel>
                                    <Select
                                      value={card.accessLevel}
                                      label="Access Level"
                                      onChange={(e) => updateTierAccessCard(card.id, 'accessLevel', e.target.value)}
                                    >
                                      <MenuItem value={TierAccessLevel.FREE}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <CheckCircleIcon color="success" fontSize="small" />
                                          Free Access
                                        </Box>
                                      </MenuItem>
                                      <MenuItem value={TierAccessLevel.PAID}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <AttachMoneyIcon color="primary" fontSize="small" />
                                          Paid Access
                                        </Box>
                                      </MenuItem>
                                      <MenuItem value={TierAccessLevel.NO_ACCESS}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <BlockIcon color="error" fontSize="small" />
                                          No Access
                                        </Box>
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} maxWidth="md" fullWidth disableEscapeKeyDown={true}>
          <DialogTitle>Delete Class - "{formData.name}"</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this class? This action will:
              </Typography>
              
              <Box sx={{ mt: 2, mb: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>What will happen:</strong>
                  </Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>The parent class will be marked as inactive</li>
                    <li>Class instances without bookings will be cancelled</li>
                    <li>Future class instances with bookings will be cancelled and members will be notified via email</li>
                    <li>Pending transactions for future classes will be cancelled (no charges)</li>
                    <li>Past class instances with bookings will be preserved for historical records</li>
                  </ul>
                </Alert>
                
                {formData.isRecurring && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      This is a recurring class. All future instances will be affected.
                    </Typography>
                  </Alert>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This operation cannot be undone. Members registered for future classes will receive cancellation notifications.
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={confirmDeleteClass} color="error" variant="contained">
              Delete Class
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default EditClassPage;